import type { ReactNode } from 'react';

// ─── Generic DataGrid types ─────────────────────────────────────────────────
//
// Entity-agnostic filter / sort / preset / export types, generalized from
// `apps/prism2/src/app/investment/TransactionTable`'s (Transaction-specific)
// `columnMeta.ts` / `transactionTableTypes.ts`. See
// `specs/property-table-landuse-aggregation.md` for the design rationale.
//
// `TRow` is the row shape for a given table (e.g. `PrismTransaction` or a
// Property row type). `K` is the string-literal union of that table's column
// keys.

/** A single enum/multi-select filter value ("value in [...]"), or a numeric range. */
export type FilterValue = string[] | { min?: number; max?: number };

/** Suffixes used to persist the two date-filter modes alongside a column key. */
export const DATE_RANGE_FILTER_SUFFIX = '__dateRange';
export const DATE_LASTDAYS_FILTER_SUFFIX = '__lastDays';

/**
 * Type guard that returns true when a FilterValue is a range object ({ min?, max? }) rather than an enum array.
 */
export function isRangeFilterValue(value: FilterValue | undefined): value is { min?: number; max?: number } {
  return !!value && !Array.isArray(value);
}

/**
 * Type guard that returns true when a FilterValue is a string array (enum/multi-select).
 */
export function isEnumFilterValue(value: FilterValue | undefined): value is string[] {
  return Array.isArray(value);
}

/** Per-column special-case behavior, injected by the consuming table. */
export interface ColumnSpecialCase<TRow> {
  /** Returns true if `row` matches `filterValue` for this column. Overrides the generic dataKey fallback. */
  filter?: (row: TRow, filterValue: FilterValue) => boolean;
  /** Comparator for sorting. Return negative/zero/positive like a normal comparator (already order-aware). */
  sort?: (a: TRow, b: TRow, order: 'asc' | 'desc') => number;
  /** Plain-text accessor used by CSV/XLSX export. Overrides the generic dataKey fallback. */
  getCellText?: (row: TRow) => string;
}

/**
 * Describes how a single DataGrid column behaves — its label, value accessor, filter/sort/aggregate/grouping configuration, and rendering/formatting overrides — consumed by `gridEngine.ts`'s generic filter/sort/aggregate/group functions.
 */
export interface ColumnMeta<TRow, K extends string = string> {
  label: string;
  /** Dot-notation path into `TRow`. Used by the generic renderer/filter/sort/export when no special case/getValue is supplied. */
  dataKey?: string;
  sortable?: boolean;
  filterable?: boolean;
  /** Which kind of filter UI/logic applies. Defaults to 'enum' when `filterable` is true. */
  filterType?: 'enum' | 'range' | 'date';
  isCurrency?: boolean;
  isDate?: boolean;
  /** Whether this column is visible in a fresh default preset. */
  defaultVisible?: boolean;
  /** Escape hatch: computed/nested value accessor, used by the generic filter/sort/export fallback in place of `dataKey`. */
  getValue?: (row: TRow) => unknown;
  /** Enum filter option list. Defaults to distinct string values derived from `dataKey`/`getValue` across the row set. */
  getFilterOptions?: (rows: TRow[]) => string[];
  /**
   * Enum mode only: customizes how a single option is displayed inside the
   * filter popover's checkbox list — e.g. a color swatch + icon for a
   * status/health column, instead of the raw option string. Receives the
   * raw option value (the same string passed to `onFilterChange`) and
   * returns the label content to render next to that option's checkbox.
   * Defaults to plain text (the raw option value) when omitted.
   */
  renderFilterOption?: (optionValue: string) => ReactNode;
  /** When set, a footer row aggregate is computed for this column across the current filtered row set. */
  aggregate?: 'sum' | 'avg' | 'count';
  /** Only relevant when `aggregate` is set — formats the computed aggregate for display. */
  formatAggregate?: (value: number) => string;
  /** Column-specific filter/sort/export overrides — the generic escape hatch. */
  specialCase?: ColumnSpecialCase<TRow>;
  /**
   * Whether this column may be selected as the "Group by" column (see
   * `groupGridRows` in `gridEngine.ts`). Typically only enum/`filterType:
   * 'enum'` columns with a small number of distinct values make sense here
   * (e.g. Division, State, Management Type) — not free-text or numeric
   * columns. Defaults to `false`.
   */
  groupable?: boolean;
  /**
   * Overrides the generic `UNGROUPED_BUCKET_LABEL` ("—") used by
   * `groupGridRows` (`gridEngine.ts`) as the bucket title when this column's
   * resolved value is missing/blank for a row. E.g. Property's State column
   * sets this to "International" so properties with no US state (their
   * `state` field is `''` by design — see `usePropertiesTableData.ts`) get a
   * meaningful group title instead of the generic "no value" dash.
   */
  emptyGroupLabel?: string;
  /**
   */
  width?: number | string;
}

/**
 * A record mapping column key string literals to their ColumnMeta descriptors for a given row type.
 */
export type ColumnMetaMap<TRow, K extends string> = Record<K, ColumnMeta<TRow, K>>;

/**
 * Persisted visibility and display-order state for a single column within a `GridPreset`.
 */
export interface ColumnConfig<K extends string = string> {
  key: K;
  visible: boolean;
  order: number;
}

/**
 * One row of clickable quick-filter "group" stat cards (e.g. Transaction's
 * phase cards, or Property's Division / Management Type cards). A table may
 * declare more than one `GroupConfig` entry to render multiple independent
 * group-card rows.
 */
export interface GroupConfigOption {
  /** The raw value stored in `GridPreset.groupFilters[groupId]` when this option is selected. */
  value: string;
  label: string;
  color?: string;
  icon?: ReactNode;
}

/**
 * Configuration for one axis of clickable quick-filter "group" stat cards (e.g. Division or Management Type), including the underlying column it filters and its selectable options.
 */
export interface GroupConfig<K extends string = string> {
  /** Stable id for this group axis — used as the key into `GridPreset.groupFilters`. */
  id: string;
  /** The column this group axis logically filters (for display/consistency only). */
  field: K;
  label: string;
  options: GroupConfigOption[];
}

/**
 * Persisted, named DataGrid view state — column visibility/order, per-column filters, sort, quick-filter group selections, and row-grouping — saved/restored via the app's user preference store.
 */
export interface GridPreset<K extends string = string> {
  id: string;
  name: string;
  isDefault: boolean;
  columns: ColumnConfig<K>[];
  filters: Record<string, FilterValue>;
  sortColumns: string[];
  sortOrders: ('asc' | 'desc' | 'none')[];
  /** Map of GroupConfig.id -> selected option values for that group axis. */
  groupFilters: Record<string, string[]>;
  /**
   * The single column to row-group by (distinct from `groupFilters` above,
   * which drives the quick-filter stat cards). When set, the table renders
   * one collapsible summary row per distinct value of this column (via
   * `groupGridRows` in `gridEngine.ts`), each expandable to its member rows.
   * `undefined`/`null` means no row grouping (the default flat-row behavior).
   */
  groupByColumnKey?: K | null;
  /** Only set on the "last state" preset — which named preset it was derived from. */
  basePresetId?: string;
}

/** Sentinel id for the auto-saved "last state" preset (mirrors TransactionTable's LAST_PRESET_ID). */
export const LAST_PRESET_ID = '__last__';

/**
 * Creates a default GridPreset with the given columns, marking it as the default preset with no filters applied.
 */
export function buildDefaultPreset<K extends string>(columns: ColumnConfig<K>[]): GridPreset<K> {
  return {
    id: 'default',
    name: 'Default',
    isDefault: true,
    columns,
    filters: {},
    sortColumns: [],
    sortOrders: [],
    groupFilters: {},
  };
}

/**
 * Creates a "Last" preset copy of a default preset by copying its shape, changing the id to `LAST_PRESET_ID`, and marking it as non-default.
 */
export function buildLastPreset<K extends string>(defaultPreset: GridPreset<K>): GridPreset<K> {
  return {
    ...defaultPreset,
    id: LAST_PRESET_ID,
    name: 'Last',
    isDefault: false,
  };
}
